import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const SOURCE_BLOCKS_SELECTOR = '[data-cart-page-more-blocks]';
const MIRRORED_BLOCK_SCRIPT_MATCHERS = ['block-badge.js', 'block-review-summary.js'];
const MIRRORED_BLOCK_TAG_PREFIX = 'section-store--';
const CART_PAGE_BLOCKS_CACHE_TTL = 30000;
const CART_PAGE_BLOCKS_CONNECT_DELAY = 300;
const CART_PAGE_BLOCKS_AFTER_ADD_DELAY = 800;
const CART_PAGE_BLOCKS_IDLE_TIMEOUT = 2500;
const CART_PAGE_BLOCKS_DEFINITION_TIMEOUT = 1500;
const CART_PAGE_BLOCKS_SLOT_RENDER_DELAY = 350;
const CART_PAGE_BLOCKS_QUERY_PARAMS = ['preview_theme_id', '_ab', '_fd', '_sc', 'pb', 'hr'];

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #cartPageBlocksAbortController = null;
  #cartPageBlocksCache = null;
  #cartPageBlocksIdleCallback = null;
  #cartPageBlocksScheduleTimer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('click', this.#handleUpsellNavigation);
    this.addEventListener('scroll', this.#handleUpsellScroll, true);
    this.#scheduleCartPageBlocksSync({ delay: CART_PAGE_BLOCKS_CONNECT_DELAY });
    this.#queueUpsellNavigationUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('click', this.#handleUpsellNavigation);
    this.removeEventListener('scroll', this.#handleUpsellScroll, true);
    this.#cancelScheduledCartPageBlocksSync();
    this.#cartPageBlocksAbortController?.abort();
  }

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    const eventData = event.detail?.data || {};

    if (eventData.cartCountUpdateOnly) {
      this.#queueUpsellNavigationUpdate();
      return;
    }

    if (
      eventData.lightweightSectionRefreshPending ||
      eventData.cartDrawerFragmentRefresh ||
      eventData.skipCartDrawerMirrorRefresh
    ) {
      this.#cancelScheduledCartPageBlocksSync();
      this.#cartPageBlocksAbortController?.abort();
      this.#cartPageBlocksAbortController = null;
      requestAnimationFrame(() => this.#renderCachedCartPageBlocks());
      this.#queueUpsellNavigationUpdate();
      return;
    }

    requestAnimationFrame(() => {
      this.#scheduleCartPageBlocksSync({
        abortActive: true,
        delay: CART_PAGE_BLOCKS_AFTER_ADD_DELAY,
        forceRefresh: true,
      });
    });
    this.#queueUpsellNavigationUpdate();
  };

  #handleResize = () => {
    this.#queueUpsellNavigationUpdate();
  };

  #handleUpsellNavigation = (event) => {
    const control = event.target.closest('[data-cart-drawer-upsell-prev], [data-cart-drawer-upsell-next]');

    if (!control || !this.contains(control)) return;

    const upsell = control.closest('[data-cart-drawer-upsell]');
    const list = upsell?.querySelector('[data-cart-drawer-upsell-list]');

    if (!upsell || !list) return;

    const direction = control.hasAttribute('data-cart-drawer-upsell-prev') ? -1 : 1;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    list.scrollBy({
      left: list.clientWidth * direction,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });

    this.#queueUpsellNavigationUpdate(upsell);
  };

  #handleUpsellScroll = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-cart-drawer-upsell-list]')) return;

    const upsell = event.target.closest('[data-cart-drawer-upsell]');

    if (upsell) this.#queueUpsellNavigationUpdate(upsell);
  };

  open() {
    this.showDialog();
    this.#scheduleCartPageBlocksSync();
    this.#queueUpsellNavigationUpdate();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #queueUpsellNavigationUpdate(root = this) {
    requestAnimationFrame(() => this.#updateUpsellNavigation(root));
  }

  #updateUpsellNavigation(root = this) {
    const upsells = root.matches?.('[data-cart-drawer-upsell]')
      ? [root]
      : [...root.querySelectorAll('[data-cart-drawer-upsell]')];

    for (const upsell of upsells) {
      const list = upsell.querySelector('[data-cart-drawer-upsell-list]');
      const previous = upsell.querySelector('[data-cart-drawer-upsell-prev]');
      const next = upsell.querySelector('[data-cart-drawer-upsell-next]');

      if (!list || !previous || !next) continue;

      const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
      const isOverflowing = maxScroll > 1;

      previous.disabled = !isOverflowing || list.scrollLeft <= 1;
      next.disabled = !isOverflowing || list.scrollLeft >= maxScroll - 1;
      upsell.toggleAttribute('data-cart-drawer-upsell-overflowing', isOverflowing);
    }
  }

  #cartPageBlockSlots() {
    return [...this.querySelectorAll('[data-cart-drawer-mirror-block]')];
  }

  #scheduleCartPageBlocksSync({ abortActive = false, delay = 0, forceRefresh = false } = {}) {
    const slots = this.#cartPageBlockSlots();

    if (!slots.length) return;

    const url = this.#cartPageBlocksUrl();

    if (abortActive) {
      this.#cartPageBlocksAbortController?.abort();
      this.#cartPageBlocksAbortController = null;
    }

    if (this.#canUseCartPageBlocksCache(url) && !forceRefresh) {
      this.#renderCachedCartPageBlocks();
      return;
    }

    this.#cancelScheduledCartPageBlocksSync();
    this.#cartPageBlocksScheduleTimer = window.setTimeout(() => {
      this.#cartPageBlocksScheduleTimer = null;

      const syncWhenIdle = () => {
        this.#cartPageBlocksIdleCallback = null;
        this.#renderCachedCartPageBlocks(url);

        if (this.#canUseCartPageBlocksCache(url) && !forceRefresh) return;

        this.#syncCartPageBlocks(url);
      };

      if ('requestIdleCallback' in window) {
        this.#cartPageBlocksIdleCallback = window.requestIdleCallback(syncWhenIdle, {
          timeout: CART_PAGE_BLOCKS_IDLE_TIMEOUT,
        });
      } else {
        this.#cartPageBlocksScheduleTimer = window.setTimeout(syncWhenIdle, 0);
      }
    }, delay);
  }

  #cancelScheduledCartPageBlocksSync() {
    if (this.#cartPageBlocksScheduleTimer) {
      window.clearTimeout(this.#cartPageBlocksScheduleTimer);
      this.#cartPageBlocksScheduleTimer = null;
    }

    if (this.#cartPageBlocksIdleCallback && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.#cartPageBlocksIdleCallback);
      this.#cartPageBlocksIdleCallback = null;
    }
  }

  #canUseCartPageBlocksCache(url = this.#cartPageBlocksUrl()) {
    if (!this.#cartPageBlocksCache) return false;

    return (
      this.#cartPageBlocksCache.url === url.toString() &&
      Date.now() - this.#cartPageBlocksCache.timestamp < CART_PAGE_BLOCKS_CACHE_TTL
    );
  }

  #renderCachedCartPageBlocks(url = this.#cartPageBlocksUrl()) {
    if (!this.#canUseCartPageBlocksCache(url)) return;

    this.#renderCartPageBlocks(this.#cartPageBlocksCache.sourceBlocks);
  }

  async #syncCartPageBlocks(url = this.#cartPageBlocksUrl()) {
    const slots = this.#cartPageBlockSlots();

    if (!slots.length) return;

    this.#cartPageBlocksAbortController?.abort();
    this.#cartPageBlocksAbortController = new AbortController();

    const { signal } = this.#cartPageBlocksAbortController;

    try {
      let resolvedUrl = url;
      let { sourceDocument, sourceBlocks } = await this.#fetchCartPageBlockSource(resolvedUrl, signal);

      if (!this.#hasAnyCartPageBlocks(sourceBlocks, slots) && resolvedUrl.searchParams.has('section_id')) {
        resolvedUrl = this.#cartPageBlocksFallbackUrl();
        ({ sourceDocument, sourceBlocks } = await this.#fetchCartPageBlockSource(resolvedUrl, signal));
      }

      if (!this.#hasAnyCartPageBlocks(sourceBlocks, slots)) {
        throw new Error('Cart page mirror response was missing mirrored blocks');
      }

      await this.#loadMirroredBlockScripts(sourceDocument, sourceBlocks, signal);

      this.#cartPageBlocksCache = {
        sourceBlocks,
        timestamp: Date.now(),
        url: resolvedUrl.toString(),
      };

      this.#renderCartPageBlocks(sourceBlocks);
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(error);

      if (!this.#cartPageBlocksCache) {
        slots.forEach((slot) => this.#replaceSlotContent(slot, null));
      }
    }
  }

  #cartPageBlocksUrl() {
    return this.#buildCartPageBlocksUrl(this.dataset.cartDrawerMirrorSource || '/cart');
  }

  #cartPageBlocksFallbackUrl() {
    return this.#buildCartPageBlocksUrl(this.dataset.cartDrawerMirrorFallbackSource || '/cart');
  }

  #buildCartPageBlocksUrl(source) {
    const url = new URL(source, window.location.origin);
    const currentUrl = new URL(window.location.href);

    for (const key of CART_PAGE_BLOCKS_QUERY_PARAMS) {
      const value = currentUrl.searchParams.get(key);

      if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
    }

    return url;
  }

  async #fetchCartPageBlockSource(url, signal) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      signal,
    });

    if (!response.ok) throw new Error(`Cart page mirror request failed with status ${response.status}`);

    const html = await response.text();
    const sourceDocument = new DOMParser().parseFromString(html, 'text/html');

    return {
      sourceDocument,
      sourceBlocks: sourceDocument.querySelector(SOURCE_BLOCKS_SELECTOR),
    };
  }

  #hasAnyCartPageBlocks(sourceBlocks, slots) {
    if (!sourceBlocks) return false;

    return slots.some((slot) => {
      const blockId = slot.dataset.cartDrawerMirrorBlock;
      return !blockId || !!sourceBlocks.querySelector(`[id*="${blockId}"]`);
    });
  }

  #renderCartPageBlocks(sourceBlocks) {
    for (const slot of this.#cartPageBlockSlots()) {
      const blockId = slot.dataset.cartDrawerMirrorBlock;
      const sourceBlock = blockId ? sourceBlocks?.querySelector(`[id*="${blockId}"]`) : null;

      this.#replaceSlotContent(slot, sourceBlock);
    }
  }

  async #loadMirroredBlockScripts(sourceDocument, sourceBlocks, signal) {
    if (!this.#hasMissingMirroredBlockDefinitions(sourceBlocks)) return;

    const loadedScriptCount = await this.#loadMirroredBlockScriptsFromDocument(sourceDocument);
    await this.#waitForMirroredBlockDefinitions(sourceBlocks);

    if (!this.#hasMissingMirroredBlockDefinitions(sourceBlocks)) return;
    if (loadedScriptCount > 0 || !this.#cartPageBlocksUrl().searchParams.has('section_id')) return;

    const { sourceDocument: fallbackDocument } = await this.#fetchCartPageBlockSource(
      this.#cartPageBlocksFallbackUrl(),
      signal
    );

    await this.#loadMirroredBlockScriptsFromDocument(fallbackDocument);
    await this.#waitForMirroredBlockDefinitions(sourceBlocks);
  }

  async #loadMirroredBlockScriptsFromDocument(sourceDocument) {
    const scripts = [...sourceDocument.querySelectorAll('script[src]')].filter((script) =>
      MIRRORED_BLOCK_SCRIPT_MATCHERS.some((matcher) => this.#scriptSource(script).includes(matcher))
    );

    await Promise.all(scripts.map((script) => this.#loadScript(script)));

    return scripts.length;
  }

  #mirroredBlockTagNames(sourceBlocks) {
    if (!sourceBlocks) return [];

    const names = [...sourceBlocks.querySelectorAll('*')]
      .map((element) => element.localName)
      .filter((name) => name?.startsWith(MIRRORED_BLOCK_TAG_PREFIX) && name.endsWith('-block'));

    return [...new Set(names)];
  }

  #hasMissingMirroredBlockDefinitions(sourceBlocks) {
    return this.#mirroredBlockTagNames(sourceBlocks).some((name) => !customElements.get(name));
  }

  async #waitForMirroredBlockDefinitions(sourceBlocks) {
    const missingNames = this.#mirroredBlockTagNames(sourceBlocks).filter((name) => !customElements.get(name));

    if (!missingNames.length) return;

    await Promise.race([
      Promise.all(missingNames.map((name) => customElements.whenDefined(name).catch(() => {}))),
      new Promise((resolve) => window.setTimeout(resolve, CART_PAGE_BLOCKS_DEFINITION_TIMEOUT)),
    ]);
  }

  #loadScript(sourceScript) {
    const src = this.#scriptSource(sourceScript);

    if (!src || [...document.scripts].some((script) => script.src === src)) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const script = document.createElement('script');

      for (const { name, value } of sourceScript.attributes) {
        script.setAttribute(name, value);
      }

      script.src = src;
      script.onload = resolve;
      script.onerror = resolve;
      document.head.appendChild(script);
    });
  }

  #scriptSource(sourceScript) {
    const src = sourceScript.getAttribute('src') || sourceScript.src;

    if (!src) return '';

    return new URL(src, document.baseURI).href;
  }

  #replaceSlotContent(slot, sourceBlock) {
    if (!sourceBlock) {
      if (!slot.childNodes.length && slot.hidden) return;

      slot.replaceChildren();
      slot.hidden = true;
      delete slot.dataset.cartDrawerMirrorRendered;
      return;
    }

    const clonedBlock = sourceBlock.cloneNode(true);

    if (this.#slotContentMatchesSource(slot, clonedBlock)) {
      slot.hidden = false;
      this.#queueSlotRenderStateUpdate(slot);
      return;
    }

    slot.replaceChildren(clonedBlock);
    slot.hidden = false;
    this.#reactivateScripts(slot);
    this.#queueSlotRenderStateUpdate(slot);
  }

  #slotContentMatchesSource(slot, sourceBlock) {
    const currentChildren = [...slot.children];

    return currentChildren.length === 1 && currentChildren[0].isEqualNode(sourceBlock);
  }

  #reactivateScripts(container) {
    container.querySelectorAll('script').forEach((script) => {
      const replacement = document.createElement('script');

      for (const { name, value } of script.attributes) {
        replacement.setAttribute(name, value);
      }

      replacement.textContent = script.textContent;
      script.replaceWith(replacement);
    });
  }

  #queueSlotRenderStateUpdate(slot) {
    requestAnimationFrame(() => {
      this.#updateSlotRenderState(slot);
      window.setTimeout(() => this.#updateSlotRenderState(slot), CART_PAGE_BLOCKS_SLOT_RENDER_DELAY);
    });
  }

  #updateSlotRenderState(slot) {
    const hasRenderedContent =
      [...slot.children].some((child) => child.getBoundingClientRect().height > 1) ||
      [...slot.querySelectorAll('*')].some((element) => {
        const text = element.shadowRoot?.textContent || '';
        return text.trim().length > 0;
      });

    slot.toggleAttribute('data-cart-drawer-mirror-rendered', hasRenderedContent);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
